/**
 * SA-1042 (spec §D10): a customer update's proposal as a diff, the record's
 * Intake block and the Inbox triage panel alike. "Suggested by AI" with no
 * number (ruling R2: the extraction gives no confidence), every row ticked
 * (R3: a change request's replaced value is the point), Accept sends the
 * ticked rows as `extracted`. It calls no endpoint: the caller owns the write,
 * and keys it by the update id plus the change ids, so a new diff starts
 * all-ticked again.
 */
import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Badge, Button } from '@/components/ui'
import { FieldValue } from '@/components/process/FieldValue'
import { SUGGESTED_BY_AI } from '@/components/process/proposalText'
import type { CustomerUpdateApplyPayload, ProposedChange, RevisionFacts } from '@/types/commerce'
import { ACCEPT, EDIT_BEFORE_APPLYING, EDIT_IN_INTAKE, revisionLanding, revisionNotice } from './customerUpdateText'

export interface UpdateProposalProps {
  changes: ProposedChange[]
  busy: boolean
  /** The Accept button shows its working label. */
  accepting?: boolean
  /** Absent: the diff only, no ticks and no Accept (a viewer who may not apply). */
  onAccept?: (payload: CustomerUpdateApplyPayload) => void
  /** SA-1044: the revision Accept records; the notice names where the ticked rows land. */
  revision?: RevisionFacts | null
  onEdit?: () => void
  editHref?: string
}

export function UpdateProposal({ changes, busy, accepting = false, onAccept, revision, onEdit, editHref }: UpdateProposalProps) {
  const [unticked, setUnticked] = useState<Set<string>>(() => new Set())
  const taken = changes.filter((c) => !unticked.has(c.field.id))
  const notice = revision && taken.length > 0
    ? revisionNotice('Accepting', revision.number, revisionLanding(revision.steps, taken.map((c) => c.field.id)), revision.current)
    : null
  const toggle = (id: string) => setUnticked((prev) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })
  const accept = () => onAccept?.({
    slots: Object.fromEntries(taken.map((c) => [c.field.id, c.proposed])),
    provenance: Object.fromEntries(taken.map((c) => [c.field.id, 'extracted' as const])),
  })

  return (
    <div className="space-y-2" data-testid="update-proposal">
      <Badge variant="info" data-testid="proposal-chip"><Sparkles aria-hidden="true" />{SUGGESTED_BY_AI}</Badge>
      <table aria-label="Proposed changes" className="w-full text-xs">
        <thead>
          <tr>
            <th className="text-left">Field</th><th className="text-left">Current</th><th className="text-left">Proposed</th>
            {onAccept && <th>Take</th>}
          </tr>
        </thead>
        <tbody>
          {changes.map((c) => (
            <tr key={c.field.id}>
              <td>{c.field.label}</td>
              <td><FieldValue field={{ ...c.field, value: c.field.value ?? null }} /></td>
              <td><FieldValue field={{ ...c.field, value: c.proposed }} /></td>
              {onAccept && (
                <td className="text-center">
                  <input type="checkbox" aria-label={`take ${c.field.label}`} checked={!unticked.has(c.field.id)}
                    disabled={busy} onChange={() => toggle(c.field.id)} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {notice && <p className="text-xs text-muted-foreground">{notice}</p>}
      <div className="flex flex-wrap items-center gap-2">
        {onAccept && (
          <Button size="sm" disabled={busy || taken.length === 0} isLoading={accepting} onClick={accept}>
            {accepting ? 'Accepting…' : ACCEPT}
          </Button>
        )}
        {onEdit && <Button size="sm" variant="outline" disabled={busy} onClick={onEdit}>{EDIT_BEFORE_APPLYING}</Button>}
        {editHref && (
          <Link to={editHref} className="text-xs text-primary underline-offset-2 hover:underline">{EDIT_IN_INTAKE}</Link>
        )}
      </div>
    </div>
  )
}
